/**
 * Stage 2 decisions — the checklist mature validation starts from, shown at
 * the top of Intent › Design & report assumptions once early screening is
 * complete.
 *
 * Each decision shows its status, the site facts behind it, the stance of
 * every option with the evidence for and against it, the open questions the
 * screening reports raised, and any one-click proposal. Nothing is written
 * without a click: accepting a proposal applies it through the editor's own
 * draft + auto-save path (`applyIntent`), exactly like accepting a suggestion
 * chip, and records the evidence basis on the site's review state.
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import type { ProjectIntent } from '../../../types/siteRag';
import { Button } from '../../ui/button';
import { analysisCardSummary } from '../findings/analysisCardSummary';
import { stripNarrativeArtifacts } from '../../../lib/reportRendering/narrativeArtifacts';
import { STAGE2_SOURCES, STAGE2_SOURCES_REVIEWED } from '../../../lib/stage2/stage2Sources';
import { resolveStage2DecisionStatus } from '../../../lib/stage2/stage2Catalogue';
import {
  applyCoolingProposal,
  applyPhasePowerProposal,
  canRecordStage2Unknown,
  recordStage2DecisionUnknown,
  withoutStage2Deferral,
  withStage2Acceptance,
  withStage2Deferral,
} from '../../../lib/stage2/applyStage2Choice';
import type {
  BtmStrategy,
  OptionStance,
  StanceBasis,
  Stage2Decision,
  Stage2DecisionId,
  Stage2DecisionReview,
  Stage2DecisionSet,
  Stage2DecisionStatus,
  Stage2OptionAssessment,
  Stage2Proposal,
} from '../../../lib/stage2/stage2Types';

export interface Stage2DecisionsPanelProps {
  set: Stage2DecisionSet;
  /** The live (draft) intent in the editor. */
  intent: ProjectIntent;
  /** Apply a full next intent through the editor's draft + auto-save path. */
  applyIntent: (next: ProjectIntent) => void;
  btmStrategy?: BtmStrategy;
  review: Stage2DecisionReview | null;
  onSetStrategy: (value: BtmStrategy) => Promise<void>;
  onSaveReview: (review: Stage2DecisionReview) => Promise<void>;
  /** Focus an existing form field in the Design view (`data-field-id`). */
  onFocusField: (fieldId: string) => void;
  onOpenGeneration?: () => void;
  /** Active Step 1–8 stepper step ID when rendered inside the stepper workflow. */
  activeStep?: string;
  /** Callback to switch the active Step 1–8 stepper step. */
  onSelectStep?: (stepId: string) => void;
  readOnly: boolean;
  testIdPrefix?: string;
}

const STATUS_LABEL: Record<Stage2DecisionStatus, string> = {
  open: 'Open',
  decided: 'Decided',
  'recorded-unknown': 'Recorded unknown',
  deferred: 'Decide later',
  'not-applicable': 'Not applicable',
};

const STATUS_CLASS: Record<Stage2DecisionStatus, string> = {
  open: 'border-warning/40 text-warning',
  decided: 'border-success/40 text-success',
  'recorded-unknown': 'border-border-subtle text-text-secondary',
  deferred: 'border-border-subtle text-text-muted',
  'not-applicable': 'border-border-subtle text-text-muted',
};

const STANCE_LABEL: Record<OptionStance, string> = {
  favoured: 'Favoured by evidence',
  viable: 'Viable',
  constrained: 'Constrained',
  'excluded-by-evidence': 'Excluded by evidence',
  'not-assessed': 'Not assessed',
};

const STANCE_CLASS: Record<OptionStance, string> = {
  favoured: 'border-success/40 bg-success/5 text-success',
  viable: 'border-border-subtle text-text-secondary',
  constrained: 'border-warning/40 bg-warning/5 text-warning',
  'excluded-by-evidence': 'border-danger/40 bg-danger/5 text-danger',
  'not-assessed': 'border-border-subtle text-text-muted',
};

const BASIS_LABEL: Record<StanceBasis, string> = {
  regulation: 'Regulation',
  'site-evidence': 'Site evidence',
  'screening-report': 'Screening report',
  'industry-evidence': 'Industry evidence',
  'screening-policy': 'Screening policy',
  'owner-input': 'Owner input',
};

const STRATEGY_OPTIONS: ReadonlyArray<{ value: BtmStrategy; label: string }> = [
  { value: 'grid-only', label: 'Grid only' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'btm-only', label: 'Behind-the-meter only' },
  { value: 'islanded', label: 'Islanded (off-grid)' },
];

/** The existing Design-view field each decision is recorded in. */
const EDIT_FIELD: Record<Stage2DecisionId, string> = {
  'power-supply': 'phases',
  cooling: 'coolingStrategy',
  'build-phases': 'phases',
  'design-basis': 'pueValue',
  'firm-capacity': 'capacityCreditIntent',
  'water-supply': 'waterSource',
  'grid-path': 'interconnectionStatus',
  'fuel-supply': 'fuelAccess',
  nuclear: 'reactorDesignClass',
  commercial: 'offtake',
};

/** Canonical Step 1–8 ownership for each Stage 2 decision. */
export const STEP_FOR_DECISION: Record<Stage2DecisionId, string> = {
  'design-basis': 'power',
  'firm-capacity': 'power',
  'grid-path': 'power',
  'fuel-supply': 'power',
  commercial: 'power',
  'power-supply': 'phasing',
  'build-phases': 'phasing',
  cooling: 'cooling',
  'water-supply': 'cooling',
  nuclear: 'nuclear',
};

const STEP_META: Record<string, { number: number; label: string; shortLabel: string; emptyNote: string }> = {
  context: {
    number: 1,
    label: 'Step 1 · Context',
    shortLabel: '1 · Context',
    emptyNote: 'Site background prose is captured below. Stage 2 evidence decisions live in Steps 2, 4, 5, and 7.',
  },
  power: {
    number: 2,
    label: 'Step 2 · Power',
    shortLabel: '2 · Power',
    emptyNote: 'No active Stage 2 power decisions for the current strategy.',
  },
  workload: {
    number: 3,
    label: 'Step 3 · Workload',
    shortLabel: '3 · Workload',
    emptyNote: 'Workload profile was set in screening. Adjust below if your compute mix has changed.',
  },
  phasing: {
    number: 4,
    label: 'Step 4 · Phasing',
    shortLabel: '4 · Phasing',
    emptyNote: 'No active Stage 2 phasing decisions for the current strategy.',
  },
  cooling: {
    number: 5,
    label: 'Step 5 · Cooling & Water',
    shortLabel: '5 · Cooling & Water',
    emptyNote: 'No active Stage 2 cooling or water decisions for the current strategy.',
  },
  timeline: {
    number: 6,
    label: 'Step 6 · Timeline',
    shortLabel: '6 · Timeline',
    emptyNote: 'Target commercial operation date was set in screening. Refine delivery schedule below.',
  },
  nuclear: {
    number: 7,
    label: 'Step 7 · Nuclear',
    shortLabel: '7 · Nuclear',
    emptyNote: 'Nuclear reactor class is not applicable until nuclear generation is included.',
  },
  priority: {
    number: 8,
    label: 'Step 8 · Priority',
    shortLabel: '8 · Priority',
    emptyNote: 'Review optimization priority, redundancy tier, and source attestations below.',
  },
};

const STAGE2_STEP_ORDER = ['power', 'phasing', 'cooling', 'nuclear'] as const;

function Chip({ className, children, testId }: { className: string; children: React.ReactNode; testId?: string }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${className}`} data-testid={testId}>
      {children}
    </span>
  );
}

function OptionRow({ option, testId }: { option: Stage2OptionAssessment; testId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-md border border-border-subtle" data-testid={testId} data-stance={option.stance}>
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5 text-xs text-text-primary">
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {option.label}
        </span>
        <Chip className={STANCE_CLASS[option.stance]}>{STANCE_LABEL[option.stance]}</Chip>
      </button>
      {open && (
        <ul className="space-y-1.5 border-t border-border-subtle px-3 py-2">
          {option.reasons.length === 0 && (
            <li className="text-[11px] text-text-muted">No evidence bears on this option yet.</li>
          )}
          {option.reasons.map((reason, index) => (
            <li key={index} className="text-[11px] leading-snug text-text-secondary">
              <span className="mr-1.5 font-semibold text-text-muted">{BASIS_LABEL[reason.basis]}:</span>
              {analysisCardSummary(stripNarrativeArtifacts(reason.text))}
              {(reason.sourceIds ?? []).map(id => {
                const source = STAGE2_SOURCES[id];
                return source.url ? (
                  <a
                    key={id}
                    href={source.url}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-1 inline-flex items-center gap-0.5 text-primary hover:underline"
                    title={source.finding}
                  >
                    {source.publisher}
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                ) : (
                  <span key={id} className="ml-1 text-text-muted" title={source.finding}>({source.title})</span>
                );
              })}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export const Stage2DecisionsPanel: React.FC<Stage2DecisionsPanelProps> = ({
  set,
  intent,
  applyIntent,
  btmStrategy,
  review,
  onSetStrategy,
  onSaveReview,
  onFocusField,
  onOpenGeneration,
  activeStep,
  onSelectStep,
  readOnly,
  testIdPrefix = 'stage2-decisions',
}) => {
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<'step' | 'all'>('step');

  // Status reads the LIVE draft so an accepted proposal shows as decided at
  // once, before the debounced auto-save round-trips through the store.
  const decisions = useMemo(() => set.decisions.map(decision => ({
    ...decision,
    status: resolveStage2DecisionStatus(decision.id, { intent, btmStrategy, review }),
  })), [set.decisions, intent, btmStrategy, review]);

  const active = decisions.filter(decision => decision.status !== 'not-applicable');
  const notApplicable = decisions.filter(decision => decision.status === 'not-applicable');
  const openCount = active.filter(decision => decision.status === 'open').length;
  const actionable = set.available && !readOnly && !busy;

  const stepActiveDecisions = useMemo(
    () => (activeStep ? active.filter(d => STEP_FOR_DECISION[d.id] === activeStep) : active),
    [active, activeStep],
  );

  const stepCounts = useMemo(() => {
    const counts: Record<string, { total: number; open: number }> = {};
    for (const stepId of STAGE2_STEP_ORDER) {
      const items = active.filter(d => STEP_FOR_DECISION[d.id] === stepId);
      counts[stepId] = {
        total: items.length,
        open: items.filter(d => d.status === 'open').length,
      };
    }
    return counts;
  }, [active]);

  const run = async (work: () => Promise<void> | void) => {
    if (!actionable) return;
    setBusy(true); setSaveError(null);
    try { await work(); }
    catch (error) { setSaveError(error instanceof Error ? error.message : 'Could not save the decision.'); }
    finally { setBusy(false); }
  };

  const recordAcceptance = (id: Stage2DecisionId, summary: string) => onSaveReview(withStage2Acceptance(review, id, {
    basisKey: set.basisKey,
    reportIds: set.basisReportIds,
    summary,
    at: new Date().toISOString(),
  }));

  const accept = (decision: Stage2Decision, proposal: Stage2Proposal) => run(async () => {
    if (proposal.kind === 'btm-strategy') {
      await onSetStrategy(proposal.value);
    } else if (proposal.kind === 'phase-power') {
      applyIntent(applyPhasePowerProposal(intent, proposal));
    } else {
      applyIntent(applyCoolingProposal(intent, proposal));
    }
    await recordAcceptance(decision.id, proposal.summary);
  });

  const activeStepMeta = activeStep ? STEP_META[activeStep] : undefined;

  return (
    <section
      className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-3"
      data-testid={testIdPrefix}
      data-field-id="stage2-decisions"
      aria-label="Stage 2 decisions"
    >
      <header className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="type-meta font-semibold text-text-primary">Stage 2 decisions</h3>
            {activeStepMeta && (
              <span className="rounded-full border border-primary/30 bg-background/80 px-2 py-0.5 text-[10px] font-medium text-text-secondary">
                {filterMode === 'step' ? activeStepMeta.label : 'All 8 steps'}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {activeStep && active.length > 0 && (
              <div
                className="inline-flex rounded-md border border-border-subtle bg-background/80 p-0.5 text-[11px]"
                role="group"
                aria-label="Stage 2 decision filter"
                data-testid={`${testIdPrefix}-scope-toggle`}
              >
                <button
                  type="button"
                  onClick={() => setFilterMode('step')}
                  className={`rounded px-2 py-0.5 font-medium transition-colors ${
                    filterMode === 'step'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                  data-testid={`${testIdPrefix}-scope-step`}
                >
                  This step ({stepActiveDecisions.length})
                </button>
                <button
                  type="button"
                  onClick={() => setFilterMode('all')}
                  className={`rounded px-2 py-0.5 font-medium transition-colors ${
                    filterMode === 'all'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                  data-testid={`${testIdPrefix}-scope-all`}
                >
                  All steps ({active.length})
                </button>
              </div>
            )}
            {set.available && (
              <span className="text-[11px] text-text-muted" data-testid={`${testIdPrefix}-count`}>
                {openCount === 0 ? 'All reviewed' : `${openCount} open`}
              </span>
            )}
          </div>
        </div>
        <p className="text-xs text-text-muted">
          {set.available
            ? 'Early screening is complete. Mature validation starts from these decisions. Each option shows the screening evidence for and against it; a proposal is applied only when you accept it, and none is an adopted design or approval.'
            : set.unavailableReason ?? 'Stage 2 decisions open once early screening is complete.'}
        </p>
        {activeStep && active.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-1.5 pt-0.5"
            data-testid={`${testIdPrefix}-step-strip`}
          >
            <span className="text-[11px] font-medium text-text-muted">Decisions by step:</span>
            {STAGE2_STEP_ORDER.map(stepId => {
              const count = stepCounts[stepId];
              if (!count || count.total === 0) return null;
              const meta = STEP_META[stepId];
              const isCurrent = activeStep === stepId && filterMode === 'step';
              return (
                <button
                  key={stepId}
                  type="button"
                  onClick={() => {
                    setFilterMode('step');
                    onSelectStep?.(stepId);
                  }}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${
                    isCurrent
                      ? 'border-primary bg-primary/15 font-semibold text-text-primary'
                      : 'border-border-subtle bg-background/70 text-text-secondary hover:border-primary/40 hover:text-text-primary'
                  }`}
                  data-testid={`${testIdPrefix}-step-pill-${stepId}`}
                >
                  <span>Step {meta?.shortLabel ?? stepId}</span>
                  <span
                    className={`rounded-full px-1.5 py-0 text-[10px] font-semibold ${
                      count.open > 0
                        ? 'bg-warning/15 text-warning'
                        : 'bg-success/15 text-success'
                    }`}
                  >
                    {count.open > 0 ? `${count.open} open` : `${count.total} done`}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </header>
      {saveError && <p role="alert" className="type-meta text-danger">{saveError}</p>}

      {activeStep && filterMode === 'step' && stepActiveDecisions.length === 0 && active.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border-subtle bg-card/70 px-3 py-2 text-xs text-text-secondary"
          data-testid={`${testIdPrefix}-step-empty`}
        >
          <span>{activeStepMeta?.emptyNote ?? 'No Stage 2 decisions apply directly to this step.'}</span>
          <button
            type="button"
            onClick={() => setFilterMode('all')}
            className="font-medium text-primary hover:underline"
          >
            Show all {active.length} Stage 2 decisions
          </button>
        </div>
      )}

      {active.map(decision => {
        const ownerStepId = STEP_FOR_DECISION[decision.id];
        const ownerStepMeta = STEP_META[ownerStepId];
        const hiddenByStepFilter = Boolean(activeStep && filterMode === 'step' && ownerStepId !== activeStep);

        return (
          <article
            key={decision.id}
            hidden={hiddenByStepFilter}
            className="space-y-2 rounded-md border border-border-subtle bg-card p-3"
            data-field-id={decision.focusField}
            data-step-id={ownerStepId}
            data-testid={`${testIdPrefix}-decision-${decision.id}`}
            data-status={decision.status}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-sm font-semibold text-text-primary">{decision.label}</h4>
                {ownerStepMeta && (
                  <button
                    type="button"
                    onClick={() => onSelectStep?.(ownerStepId)}
                    className="inline-flex items-center rounded border border-border-subtle bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium text-text-secondary hover:border-primary/40 hover:text-text-primary"
                    title={`Jump to ${ownerStepMeta.label} form fields`}
                    data-testid={`${testIdPrefix}-${decision.id}-step-badge`}
                  >
                    {ownerStepMeta.label}
                  </button>
                )}
              </div>
              <span className="flex items-center gap-1.5">
                {decision.revisit && (
                  <Chip className="border-warning/40 text-warning" testId={`${testIdPrefix}-${decision.id}-revisit`}>Evidence changed · revisit</Chip>
                )}
                <Chip className={STATUS_CLASS[decision.status]} testId={`${testIdPrefix}-${decision.id}-status`}>{STATUS_LABEL[decision.status]}</Chip>
              </span>
            </div>
            <p className="text-xs text-text-secondary">{decision.question}</p>

            {decision.facts.length > 0 && (
              <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-[11px] sm:grid-cols-2">
                {decision.facts.map((fact, index) => (
                  <div key={index} className="flex gap-1.5">
                    <dt className="text-text-muted">{fact.label}:</dt>
                    <dd className="text-text-primary">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            )}

            {decision.options.length > 0 && (
              <ul className="space-y-1" aria-label={`${decision.label} options`}>
                {decision.options.map(option => (
                  <OptionRow key={option.optionId} option={option} testId={`${testIdPrefix}-${decision.id}-option-${option.optionId}`} />
                ))}
              </ul>
            )}

            {decision.id === 'power-supply' && !btmStrategy && set.available && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testIdPrefix}-strategy`}>
                <span className="text-[11px] text-text-muted">Power strategy:</span>
                {STRATEGY_OPTIONS.map(option => (
                  <Button
                    key={option.value}
                    type="button"
                    size="xs"
                    variant="outline"
                    disabled={!actionable}
                    onClick={() => void run(() => onSetStrategy(option.value))}
                    data-testid={`${testIdPrefix}-strategy-${option.value}`}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            )}

            {set.available && (decision.status === 'open' || decision.status === 'deferred') && decision.proposals.map((proposal, index) => (
              <div
                key={index}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/30 px-2.5 py-1.5"
                data-testid={`${testIdPrefix}-${decision.id}-proposal-${proposal.kind}`}
              >
                <span className="text-[11px] text-text-primary">
                  <span className="mr-1 font-semibold">Proposal:</span>{proposal.summary}
                </span>
                <Button
                  type="button"
                  size="xs"
                  disabled={!actionable}
                  onClick={() => void accept(decision, proposal)}
                  data-testid={`${testIdPrefix}-${decision.id}-accept-${proposal.kind}`}
                >
                  Accept
                </Button>
              </div>
            ))}

            {decision.openQuestions.length > 0 && (
              <ul className="space-y-0.5 text-[11px] text-text-secondary" aria-label="Open questions">
                {decision.openQuestions.map((question, index) => (
                  <li key={index}>
                    <span className="font-semibold text-text-muted">Open question: </span>
                    {analysisCardSummary(stripNarrativeArtifacts(question.question))}
                    {question.followUp ? <span className="text-text-muted"> — {analysisCardSummary(stripNarrativeArtifacts(question.followUp))}</span> : null}
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              <Button
                type="button"
                size="xs"
                variant="ghost"
                onClick={() => {
                  onSelectStep?.(ownerStepId);
                  onFocusField(EDIT_FIELD[decision.id]);
                }}
                data-testid={`${testIdPrefix}-${decision.id}-edit`}
              >
                Edit fields
              </Button>
              {set.available && decision.status === 'open' && canRecordStage2Unknown(decision.id) && (
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  disabled={!actionable}
                  onClick={() => void run(() => applyIntent(recordStage2DecisionUnknown(intent, decision.id)))}
                  data-testid={`${testIdPrefix}-${decision.id}-unknown`}
                >
                  Record unknown
                </Button>
              )}
              {set.available && decision.status === 'open' && (
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  disabled={!actionable}
                  onClick={() => void run(() => onSaveReview(withStage2Deferral(review, decision.id, set.basisKey, new Date().toISOString())))}
                  data-testid={`${testIdPrefix}-${decision.id}-defer`}
                >
                  Decide later
                </Button>
              )}
              {set.available && decision.status === 'deferred' && (
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  disabled={!actionable}
                  onClick={() => void run(() => onSaveReview(withoutStage2Deferral(review, decision.id)))}
                  data-testid={`${testIdPrefix}-${decision.id}-resume`}
                >
                  Review now
                </Button>
              )}
            </div>
          </article>
        );
      })}

      {notApplicable.length > 0 && (
        <p className="text-[11px] text-text-muted" data-testid={`${testIdPrefix}-not-applicable`}>
          Not applicable to the current strategy: {notApplicable.map(decision => decision.label).join(', ')}.
        </p>
      )}

      <footer className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] text-text-muted">
          Stances cite public sources reviewed {STAGE2_SOURCES_REVIEWED}. “Screening policy” marks platform judgement, not a regulation or measured fact.
        </p>
        {set.available && onOpenGeneration && (
          <Button type="button" size="sm" variant="outline" onClick={onOpenGeneration} data-testid={`${testIdPrefix}-open-generation`}>
            Continue to Generation &amp; Impact
          </Button>
        )}
      </footer>
    </section>
  );
};

export interface Stage2ReadyBannerProps {
  openCount: number;
  onReview: () => void;
  testIdPrefix?: string;
}

/** Screening-view prompt shown once early screening is complete. */
export const Stage2ReadyBanner: React.FC<Stage2ReadyBannerProps> = ({ openCount, onReview, testIdPrefix = 'stage2-ready' }) => (
  <div
    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2"
    data-testid={testIdPrefix}
  >
    <p className="text-xs text-text-primary">
      Early screening is complete.{' '}
      {openCount > 0
        ? `${openCount} Stage 2 decision${openCount === 1 ? ' is' : 's are'} ready to review, each with the screening evidence for its options.`
        : 'Review the Stage 2 decisions recorded for mature validation.'}
    </p>
    <Button type="button" size="sm" onClick={onReview} data-testid={`${testIdPrefix}-review`}>
      Review Stage 2 decisions
    </Button>
  </div>
);
